import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2 } from "lucide-react";
import { pdfDownloadUrl } from "@/lib/pdfDownload";
import { trackCta } from "@/lib/trackCta";

const schema = z.object({
  name: z.string().trim().max(100).optional().or(z.literal("")),
  email: z.string().trim().email("Enter a valid email").max(254),
});

export const SevenSignsOptIn = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = schema.safeParse({ name, email });
    if (!parsed.success) {
      const fieldErrors: { name?: string; email?: string } = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === "name" || field === "email") {
          if (!fieldErrors[field]) fieldErrors[field] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      const cleanName = parsed.data.name?.trim() || null;
      const cleanEmail = parsed.data.email;

      // Server-side handles encryption, subscriber records, rate limiting, and email send.
      const { error: sendError } = await supabase.functions.invoke("send-7-signs-pdf", {
        body: { email: cleanEmail, name: cleanName },
      });
      if (sendError) throw sendError;

      // Auto-trigger the PDF download in the browser (tracked).
      try {
        const link = document.createElement("a");
        link.href = pdfDownloadUrl("7-signs", "7-signs-optin-auto");
        link.download = "7-signs-wake-up-guide.pdf";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (downloadErr) {
        console.error("Auto-download failed", downloadErr);
      }

      setDone(true);
    } catch (err) {
      console.error(err);
      toast({
        title: "Something went wrong",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <p className="text-sm uppercase tracking-widest text-foreground/60">
            Something I wrote down for myself
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground leading-tight">
            7 Signs You're Numbing Out as a Dad —{" "}
            <span className="text-accent">And How to Wake Up</span>
          </h2>
          <p className="text-lg text-foreground/80 leading-relaxed">
            These are seven things I missed in myself for years. I'm not telling
            you what to do with them — I'm just naming what I saw when I finally
            looked. If you want a copy, leave your email and I'll send it over.
          </p>

          {done ? (
            <div className="bg-card border border-border rounded-lg p-8 space-y-4 text-left">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                <h3 className="font-display text-xl font-bold text-foreground">
                  You're on the list.
                </h3>
              </div>
              <p className="text-foreground/80 leading-relaxed">
                Your copy of <em>7 Signs You're Numbing Out as a Dad — And
                How to Wake Up</em> should be downloading now. I also sent it
                to your inbox so you don't lose it. If it doesn't show up,
                check spam.
              </p>
              <p className="text-foreground/80 leading-relaxed">
                If the download didn't start, grab it here:
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  asChild
                  className="bg-accent text-white hover:bg-accent/90"
                >
                  <a
                    href={pdfDownloadUrl("7-signs", "7-signs-optin-fallback")}
                    rel="noopener"
                    onClick={() =>
                      trackCta(
                        "home_seven_signs_pdf_fallback",
                        pdfDownloadUrl("7-signs", "7-signs-optin-fallback"),
                        "Download the PDF (fallback)",
                      )
                    }
                  >
                    Download the PDF
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <Link
                    to="/7-signs"
                    onClick={() =>
                      trackCta(
                        "home_seven_signs_read",
                        "/7-signs",
                        "Read it on the site",
                      )
                    }
                  >
                    Read it on the site
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              <form
                onSubmit={handleSubmit}
                noValidate
                className="flex flex-col gap-3 max-w-md mx-auto pt-4 text-left"
              >
                <div>
                  <Input
                    id="seven-signs-name"
                    type="text"
                    placeholder="Your first name (optional)"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) setErrors((p) => ({ ...p, name: undefined }));
                    }}
                    maxLength={100}
                    aria-label="Your first name (optional)"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "seven-signs-name-error" : undefined}
                    className="h-12 border-2 border-border focus:border-accent"
                  />
                  {errors.name && (
                    <p
                      id="seven-signs-name-error"
                      role="alert"
                      className="mt-1.5 text-sm text-destructive"
                    >
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Input
                    id="seven-signs-email"
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
                    }}
                    required
                    maxLength={254}
                    aria-label="Your email"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "seven-signs-email-error" : undefined}
                    className="h-12 border-2 border-border focus:border-accent"
                  />
                  {errors.email && (
                    <p
                      id="seven-signs-email-error"
                      role="alert"
                      className="mt-1.5 text-sm text-destructive"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  size="lg"
                  className="bg-accent text-white hover:bg-accent/90 h-12 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send It to Me"}
                </Button>
              </form>
              <p className="text-sm text-foreground/60">
                No spam. No programs. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
